import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  DxGanttComponent,
  DxGanttModule,
  DxPopupModule,
  DxButtonModule,
  DxCheckBoxComponent,
  DxPopoverComponent,
} from 'devextreme-angular';
import { ProjectData, Schedule } from '../../project-creation-form.component';
import { ScheduleService } from 'src/app/services/schedule-item.service';
import { TemplatesService } from 'src/app/services/templates.service';
import { Subscription } from 'rxjs';
import { GanttTask, GanttDependancy } from 'src/app/pages/config/project-config/config-schedule/config-schedule.types';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GenericPopupComponent } from 'src/app/components/generic-popup/generic-popup.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import {
  ContextMenuPreparingEvent,
  DependencyDeletedEvent,
  DependencyInsertedEvent,
  DependencyInsertingEvent,
  TaskUpdatingEvent,
} from 'devextreme/ui/gantt';
import { GanttDependencyType } from 'src/app/pages/config/project-config/config-schedule/config-schedule.types';
import { ValueChangedEvent } from 'devextreme/ui/switch';
import { AppGlobals } from 'src/app/app.globals';

@Component({
    selector: 'app-project-schedule-step',
    imports: [
        CommonModule,
        DxGanttModule,
        DxPopupModule,
        DxButtonModule,
        DxCheckBoxComponent,
        DxPopoverComponent,
        GpdButtonComponent,
        GenericPopupComponent,
        SvgIconComponent,
    ],
    templateUrl: './project-schedule-step.component.html',
    styleUrls: ['./project-schedule-step.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectScheduleStepComponent implements OnInit, OnDestroy {
  @Input() public projectData: ProjectData;
  @Input() public schedule: Schedule[] = [];
  @Input() public validationGroup: string;
  @Output() public projectDataChange = new EventEmitter<ProjectData>();
  @Output() public scheduleChange = new EventEmitter<Schedule[]>();
  public ganttTasks: GanttTask[] = [];
  public ganttDependancies: GanttDependancy[] = [];

  // Expanded popup properties
  public expandedPopupVisible = false;

  // Date calculations based on project start date
  public projectStartDate: Date;

  public currentdate = new Date();

  public typeColors = {
    Milestone: '#00A4EF',
    Package: '#629D37',
    Phase: '#8B5CF6',
  };

  public loading = false;
  public subscriptions: Subscription[] = [];

  // Safe mode and circular dependency state
  public safeMode: boolean = true;
  public showDisableDeps = false;
  public showCircularDepError = false;
  public dontshowAgain = false;
  public isAdmin: boolean = AppGlobals.UserPermissions.isAdministrator;

  @ViewChild('minifiedGantt') public minifiedGantt: DxGanttComponent;
  @ViewChild('expandedGantt') public expandedGantt: DxGanttComponent;

  constructor(
    private cdr: ChangeDetectorRef,
    private scheduleService: ScheduleService,
    private templatesService: TemplatesService
  ) {}

  public async ngOnInit() {
    // Load "don't show again" preference from localStorage
    this.dontshowAgain = localStorage.getItem('dependancyDisableWarning') === 'true';

    if (this.projectData?.startDate) {
      this.projectStartDate = new Date(this.projectData.startDate);
    }

    // Schedule is now provided as input with actual dates, so map to gantt tasks
    if (this.schedule.length > 0) {
      this.calculateDatesAndMapTasks();
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onContextMenuPreparing(e: ContextMenuPreparingEvent) {
    if (e.targetType === 'task') {
      e.cancel = true;
    }
  }

  public calculateDatesAndMapTasks() {
    this.ganttTasks = [];
    this.ganttDependancies = [];

    // Build dependencies array
    this.schedule.forEach(item => {
      if (item.dependencyOnId !== null && item.dependencyOnId !== undefined) {
        this.ganttDependancies.push({
          dependancyOnId: item.dependencyOnId,
          dependencyType: item.dependencyType,
          id: this.ganttDependancies.length,
          taskId: item.id,
        });
      }
    });

    // Map schedule items directly to gantt tasks since they already have actual dates
    this.ganttTasks = this.schedule.map(item => {
      return {
        id: item.id,
        parentId: item.parentId,
        title: item.title,
        startDate: new Date(item.startDate),
        endDate: new Date(item.endDate),
        color: this.typeColors[item.itemType],
        progress: item.progress ?? 0,
        sortingIndex: item.sortingIndex,
      };
    });
    console.log('ganttTasks', this.ganttTasks);

    this.cdr.markForCheck();
  }

  public showExpandedGantt() {
    this.expandedPopupVisible = true;
    this.cdr.detectChanges();
  }

  public hideExpandedGantt() {
    this.expandedPopupVisible = false;
    this.cdr.detectChanges();
  }

  public onProjectStartDateChange() {
    // Recalculate all dates when start date changes
    this.calculateDatesAndMapTasks();
  }

  // Method to emit schedule changes when user customizes the schedule
  public onScheduleCustomized(updatedSchedule: Schedule[]) {
    this.scheduleChange.emit(updatedSchedule);
  }

  // Handle task updating event (before update)
  public onTaskUpdating(e: TaskUpdatingEvent) {
    let newStart = e.newValues.startDate ?? e.values.startDate;
    let newEnd = e.newValues.endDate ?? e.values.endDate;

    // Round to whole days
    newStart = new Date(Math.round(newStart.getTime() / 1000 / 60 / 60 / 24) * 1000 * 60 * 60 * 24);
    newEnd = new Date(Math.round(newEnd.getTime() / 1000 / 60 / 60 / 24) * 1000 * 60 * 60 * 24);

    // Ensure minimum duration of 1 day
    if (e.newValues.startDate) {
      const dur = (newEnd.getTime() - newStart.getTime()) / 1000 / 60 / 60 / 24;
      if (dur < 1) {
        e.newValues.startDate = new Date(newEnd.getTime() - 24 * 60 * 60 * 1000);
      }
    }
    if (e.newValues.endDate) {
      const dur = (newEnd.getTime() - newStart.getTime()) / 1000 / 60 / 60 / 24;
      if (dur < 1) {
        e.newValues.endDate = new Date(newStart.getTime() + 24 * 60 * 60 * 1000);
      }
    }
  }

  // Handle task updates from Gantt chart
  public onTaskUpdated(e: any) {
    const ganttTask = this.ganttTasks.find(t => t.id === e.key);
    if (ganttTask) {
      // Update the gantt task with new values
      if (e.values.startDate) {
        ganttTask.startDate = new Date(e.values.startDate);
        ganttTask.startDate.setHours(0, 0, 0, 0);
      }
      if (e.values.endDate) {
        ganttTask.endDate = new Date(e.values.endDate);
        ganttTask.endDate.setHours(0, 0, 0, 0);
      }

      // Update dependencies if needed
      this.calculateDependants(ganttTask);
    }

    this.updateScheduleFromGantt();
  }

  // Handle task insertions from Gantt chart
  public onTaskInserted(e: any) {
    this.updateScheduleFromGantt();
  }

  // Handle task deletions from Gantt chart
  public onTaskDeleted(e: any) {
    this.updateScheduleFromGantt();
  }

  // Handle dependency insertion
  public onDependencyInserted(e: DependencyInsertedEvent) {
    const task = this.schedule.find(s => s.id === e.values.taskId);
    const ganttTask = this.ganttTasks.find(s => s.id === e.values.taskId);

    if (task) {
      if (task.progress == 100) {
        return;
      }

      task.dependencyOnId = e.values.dependancyOnId;
      task.dependencyType = e.values.dependencyType;
    }

    if (ganttTask) {
      this.calculateDependants(ganttTask);
    }
  }

  // Handle dependency deletion
  public onDependencyDeleted(e: DependencyDeletedEvent) {
    const task = this.schedule.find(s => s.id === e.values.taskId);

    if (task) {
      task.dependencyOnId = null;
      task.dependencyType = null;
    }
  }

  // Calculate dependent tasks when a task is updated
  private calculateDependants(task: GanttTask) {
    const ganttDeps = this.ganttDependancies.filter(s => s.dependancyOnId === task.id);

    for (const dep of ganttDeps) {
      const dependentTask = this.ganttTasks.find(s => s.id === dep.taskId);
      const depScheduleItem = this.schedule.find(s => s.id === dep.taskId);

      if (!dependentTask || !depScheduleItem) {
        continue;
      }

      let relativeDate = new Date(this.projectStartDate);

      // Determine relative date based on dependency type
      switch (dep.dependencyType) {
        case GanttDependencyType.FF:
        case GanttDependencyType.FS:
          relativeDate = new Date(task.endDate);
          break;
        case GanttDependencyType.SS:
        case GanttDependencyType.SF:
          relativeDate = new Date(task.startDate);
          break;
      }
      relativeDate.setHours(0, 0, 0, 0);

      const currentDuration =
        (dependentTask.endDate.getTime() - dependentTask.startDate.getTime()) / 1000 / 60 / 60 / 24;

      // Update dependent task dates based on dependency type
      switch (dep.dependencyType) {
        case GanttDependencyType.SS:
        case GanttDependencyType.FS:
          dependentTask.startDate = new Date(relativeDate);
          dependentTask.endDate = new Date(relativeDate.getTime() + currentDuration * 24 * 60 * 60 * 1000);
          break;
        case GanttDependencyType.SF:
        case GanttDependencyType.FF:
          dependentTask.endDate = new Date(relativeDate);
          dependentTask.startDate = new Date(relativeDate.getTime() - currentDuration * 24 * 60 * 60 * 1000);
          break;
      }

      // Recursively calculate dependants
      this.calculateDependants(dependentTask);
    }
  }

  // Convert current Gantt tasks back to Schedule format and emit changes
  private updateScheduleFromGantt() {
    if (!this.ganttTasks || this.ganttTasks.length === 0) {
      return;
    }

    const updatedSchedule: Schedule[] = this.ganttTasks.map(ganttTask => {
      // Find the corresponding original schedule item to preserve additional properties
      const originalScheduleItem = this.schedule.find(s => s.id === ganttTask.id);

      return {
        id: ganttTask.id,
        parentId: ganttTask.parentId,
        title: ganttTask.title,
        itemType: originalScheduleItem?.itemType || 'Task',
        startDate: new Date(ganttTask.startDate),
        endDate: new Date(ganttTask.endDate),
        dependencyOnId: originalScheduleItem?.dependencyOnId,
        dependencyType: originalScheduleItem?.dependencyType,
        sortingIndex: ganttTask.sortingIndex,
      };
    });

    this.onScheduleCustomized(updatedSchedule);
  }

  public onPopupShown() {
    setTimeout(() => {
      this.expandedGantt.visible = true;
      this.cdr.markForCheck();
    }, 100);
  }

  public getDuration(startDate: Date, endDate: Date) {
    const diff = endDate.getTime() - startDate.getTime();
    const diffDays = Math.floor(diff / (24 * 60 * 60 * 1000));
    return diffDays;
  }

  onDependencyInserting(e: DependencyInsertingEvent) {
    const succ = this.schedule.find(s => s.id == e.values.taskId);

    if (succ.itemType != 'Package' || succ.progress == 100) {
      e.cancel = true;
      return;
    }

    // Cancel if successor already has a dependency
    if (succ.dependencyOnId) {
      e.cancel = true;
      return;
    }

    // Check for circular dependency
    if (this.wouldCreateCircularDependency(e.values.taskId, e.values.dependancyOnId)) {
      this.showCircularDepError = true;
      this.cdr.markForCheck();
      e.cancel = true;
    }
  }

  /**
   * Checks if adding a dependency from predecessorId -> successorId would create a circular dependency.
   * This traverses the dependency chain starting from predecessorId to see if it eventually leads back to successorId.
   * Also checks children of the predecessor since parent dates are determined by their children.
   */
  private wouldCreateCircularDependency(successorId: number, predecessorId: number): boolean {
    const visited = new Set<number>();
    const queue: number[] = [predecessorId];

    // Also add all children of the predecessor (recursively) since parent dates are determined by children
    const addChildrenToQueue = (parentId: number) => {
      const children = this.schedule.filter(s => s.parentId == parentId);
      for (const child of children) {
        if (!visited.has(child.id)) {
          queue.push(child.id);
          addChildrenToQueue(child.id);
        }
      }
    };
    addChildrenToQueue(predecessorId);

    while (queue.length > 0) {
      const currentId = queue.shift();

      if (currentId == successorId) {
        return true;
      }

      if (visited.has(currentId)) {
        continue;
      }
      visited.add(currentId);

      // Find the task that currentId depends on
      const currentTask = this.schedule.find(s => s.id == currentId);
      if (currentTask?.dependencyOnId) {
        queue.push(currentTask.dependencyOnId);
        // Also add children of the dependency target
        addChildrenToQueue(currentTask.dependencyOnId);
      }
    }

    return false;
  }

  onSafeModeChanged(e: ValueChangedEvent) {
    if (e.value === false && e.previousValue === true && !this.dontshowAgain) {
      this.showDisableDeps = true;
    }
    this.minifiedGantt?.instance.repaint();
    this.expandedGantt?.instance.repaint();
  }

  onDontShowAgainChanged(e: ValueChangedEvent) {
    if (e.value === true) {
      this.dontshowAgain = true;
      localStorage.setItem('dependancyDisableWarning', 'true');
    } else {
      this.dontshowAgain = false;
      localStorage.removeItem('dependancyDisableWarning');
    }
  }
}
